// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Estoque/PendenciasRenave.tsx
//  RENAVE Fatia 4: painel de pendências + conciliação com o RENAVE.
//
//  Pendências vêm do banco (rápido). A conciliação relê no RENAVE cada veículo
//  aberto: aplica transferências consumidas, marca divergências e lista estoques
//  que o Gestor não conhece. Ao abrir, concilia sozinha se a última tem > 24 h.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ClipboardList, Info, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getApiErrorMessage } from '@/lib/apiClient';
import { toast } from '@/components/ui/sonner';
import { renaveApi, TIPO_PENDENCIA_LABEL, type Pendencia } from '@/features/renave/api';

const UM_DIA_MS = 24 * 60 * 60 * 1000;

const SEVERIDADE: Record<Pendencia['severidade'], { rotulo: string; variante: 'destructive' | 'default' | 'outline' }> = {
  ALTA:  { rotulo: 'Alta',  variante: 'destructive' },
  MEDIA: { rotulo: 'Média', variante: 'default' },
  INFO:  { rotulo: 'Info',  variante: 'outline' },
};

export default function PendenciasRenave() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dias, setDias] = useState('3');
  const [conciliando, setConciliando] = useState(false);
  const autoFeita = useRef(false);

  const q = useQuery({ queryKey: ['renave-pendencias', dias], queryFn: () => renaveApi.pendencias(Number(dias)) });
  const painel = q.data;

  const conciliar = async (automatica = false) => {
    setConciliando(true);
    try {
      const c = await renaveApi.conciliar();
      if (c.falhas > 0) toast.warning(c.resumo, { duration: 12_000 });
      else if (!automatica || c.atualizados > 0 || c.divergencias > 0 || c.desconhecidosNoGestor.length > 0)
        toast.success(c.resumo, { duration: 10_000 });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['renave-pendencias'] }),
        qc.invalidateQueries({ queryKey: ['veiculos-estoque'] }),
      ]);
    } catch (e) {
      if (!automatica) toast.error(getApiErrorMessage(e));
    } finally {
      setConciliando(false);
    }
  };

  // Conciliação "periódica" pelo uso: ao abrir o painel, se a última tem mais de 24 h.
  useEffect(() => {
    if (!painel?.ativo || autoFeita.current) return;
    const ultima = painel.ultimaConciliacao ? new Date(painel.ultimaConciliacao).getTime() : 0;
    if (Date.now() - ultima > UM_DIA_MS) {
      autoFeita.current = true;
      void conciliar(true);
    }
  }, [painel?.ativo, painel?.ultimaConciliacao]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendencias = painel?.pendencias ?? [];
  const altas = pendencias.filter(p => p.severidade === 'ALTA').length;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/estoque/veiculos')} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <ClipboardList className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Pendências do RENAVE</h1>
            <p className="text-sm text-muted-foreground">
              O que falta enviar ou conferir para o estoque do Gestor bater com o RENAVE
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={dias} onValueChange={setDias}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['1', '3', '7', '15'].map(d => (
                <SelectItem key={d} value={d}>Detran atrasado após {d} dia{d === '1' ? '' : 's'}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => void conciliar()} disabled={conciliando || !painel?.ativo}>
            {conciliando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Conciliar com o RENAVE
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Última conciliação</CardTitle>
          <CardDescription>
            {painel?.ultimaConciliacao
              ? new Date(painel.ultimaConciliacao).toLocaleString('pt-BR')
              : 'Nunca conciliado.'}
            {conciliando && ' · conciliando agora…'}
          </CardDescription>
        </CardHeader>
        {painel?.resumoConciliacao && (
          <CardContent className="text-sm">{painel.resumoConciliacao}</CardContent>
        )}
      </Card>

      {painel && !painel.ativo && (
        <p className="text-sm text-muted-foreground">O RENAVE não está ativo para esta empresa (Configurações → RENAVE).</p>
      )}

      <div className="flex items-center gap-2 text-sm">
        {altas > 0
          ? <><AlertTriangle className="h-4 w-4 text-destructive" /><span>{altas} pendência(s) de prioridade alta.</span></>
          : <><Info className="h-4 w-4 text-muted-foreground" /><span className="text-muted-foreground">Nenhuma pendência de prioridade alta.</span></>}
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Prioridade</TableHead>
              <TableHead>Veículo</TableHead>
              <TableHead>Pendência</TableHead>
              <TableHead>O que fazer</TableHead>
              <TableHead>Desde</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>
            ) : q.error ? (
              <TableRow><TableCell colSpan={5} className="text-center text-destructive">{getApiErrorMessage(q.error)}</TableCell></TableRow>
            ) : !pendencias.length ? (
              <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">Nenhuma pendência. Estoque em dia com o RENAVE.</TableCell></TableRow>
            ) : pendencias.map((p, i) => (
              <TableRow key={`${p.veiculoId}-${p.tipo}-${i}`} className="cursor-pointer" onClick={() => navigate(`/estoque/veiculos/${p.veiculoId}`)}>
                <TableCell><Badge variant={SEVERIDADE[p.severidade].variante}>{SEVERIDADE[p.severidade].rotulo}</Badge></TableCell>
                <TableCell>
                  <div className="font-medium">{p.descricao ?? 'Veículo'}</div>
                  <div className="font-mono text-xs text-muted-foreground">{p.placa ?? p.chassi}</div>
                </TableCell>
                <TableCell className="text-sm">{TIPO_PENDENCIA_LABEL[p.tipo] ?? p.tipo}</TableCell>
                <TableCell className="max-w-md text-xs">{p.mensagem}</TableCell>
                <TableCell className="whitespace-nowrap text-xs">{p.desde ? new Date(p.desde).toLocaleDateString('pt-BR') : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
